import { useState, useEffect, useCallback, useRef } from 'react';
import { Image, Upload, Wand2, Search, X, RefreshCw, Trash2, Edit3, Check, Loader2, Plus, Sparkles, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { mediaApi } from '@/lib/mediaApi';
import api from '@/lib/api';
import { OrgMatchDialog, PersonMatchDialog } from '@/components/EntityMatchDialog';

const IMAGE_TYPES  = ['uploaded', 'stock', 'ai_generated', 'logo', 'licensed'];
const ORIENTATIONS = ['landscape', 'portrait', 'square', 'unknown'];

const EMPTY_FILTERS = {
  search: '', image_type: '', orientation: '', tags: '',
  organisation_id: '', organisation_name: '', person_id: '', person_name: '',
};

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function EntitySearchCombobox({ entityType, value, onSelect, onClear, placeholder, onCreateRequest }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const debouncedQuery        = useDebounce(query, 150);
  const wrapperRef            = useRef(null);
  const cache                 = useRef({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) { setResults([]); setLoading(false); return; }
    const cacheKey = `${entityType}:${debouncedQuery.toLowerCase()}`;
    if (cache.current[cacheKey]) { setResults(cache.current[cacheKey]); return; }
    setLoading(true);
    const endpoint = entityType === 'organisation' ? '/organisations/match' : '/persons/match';
    api.get(endpoint, { params: { q: debouncedQuery } })
      .then(res => { const d = res.data || []; cache.current[cacheKey] = d; setResults(d); })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [debouncedQuery, entityType]);

  if (value) {
    return (
      <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
        <div>
          <p className="text-sm font-medium text-foreground">{value.name}</p>
          {value.title && <p className="text-xs text-muted-foreground">{value.title}</p>}
        </div>
        <button onClick={onClear} className="text-muted-foreground hover:text-red-500 ml-2">
          <X size={13} />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || `Search ${entityType}s...`}
          className="w-full pl-8 pr-8 py-2 border border-border rounded-lg text-sm bg-background text-foreground placeholder:text-muted-foreground"
        />
        {loading
          ? <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
          : query && <button onClick={() => { setQuery(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X size={13} /></button>
        }
      </div>
      {open && query.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-background border border-border rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 size={13} className="animate-spin" /> Searching...
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No results found</div>
          )}
          {!loading && results.map(r => (
            <button
              key={r.id}
              onClick={() => { onSelect(r); setQuery(''); setOpen(false); setResults([]); }}
              className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <span className="text-foreground font-medium">{r.name}</span>
                {r.title && <span className="text-xs text-muted-foreground ml-2">{r.title}</span>}
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{Math.round((r.confidence || 0) * 100)}%</span>
            </button>
          ))}
          {!loading && query.trim().length >= 2 && onCreateRequest && (
            <button
              onClick={() => { onCreateRequest(query.trim()); setOpen(false); setQuery(''); setResults([]); }}
              className="w-full text-left px-3 py-2 hover:bg-[#FFF4E8] text-sm flex items-center gap-2 border-t border-border text-blue-600"
            >
              <Plus size={13} /> Create "{query.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TypeBadge({ type }) {
  const colours = {
    uploaded:     'bg-blue-100 text-blue-700',
    stock:        'bg-purple-100 text-purple-700',
    ai_generated: 'bg-amber-100 text-amber-700',
    logo:         'bg-green-100 text-green-700',
    licensed:     'bg-rose-100 text-rose-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colours[type] || 'bg-gray-100 text-gray-600'}`}>
      {type?.replace('_', ' ')}
    </span>
  );
}

function OrientationIcon({ orientation }) {
  const map = { landscape: '▬', portrait: '▮', square: '■', unknown: '?' };
  return <span className="text-xs text-muted-foreground">{map[orientation] || '?'}</span>;
}

function AssetCard({ asset, onSelect, selected }) {
  return (
    <div
      onClick={() => onSelect(asset)}
      className={`relative group cursor-pointer rounded-xl overflow-hidden border transition-all ${
        selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-border hover:border-[#C9B49A]'
      }`}
    >
      <div className="aspect-video bg-muted relative">
        <img
          src={asset.thumbnail_url || asset.file_url}
          alt={asset.alt_text || asset.filename}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = '/placeholder-image.png'; }}
        />
        {selected && (
          <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
            <Check size={24} className="text-blue-600 bg-white rounded-full p-1" />
          </div>
        )}
        <div className="absolute top-2 left-2"><TypeBadge type={asset.image_type} /></div>
        <div className="absolute top-2 right-2"><OrientationIcon orientation={asset.orientation} /></div>
      </div>
      <div className="p-2">
        <p className="text-xs font-medium text-foreground truncate">{asset.filename}</p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-muted-foreground">Used {asset.usage_count}x</p>
          {(asset.organisation_id || asset.person_id) && (
            <span className="text-xs text-muted-foreground">{asset.organisation_id ? 'Org' : 'Person'} linked</span>
          )}
        </div>
        {asset.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {asset.tags.slice(0, 3).map(tag => (
              <span key={tag} className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{tag}</span>
            ))}
            {asset.tags.length > 3 && <span className="text-xs text-muted-foreground">+{asset.tags.length - 3}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function AssetDrawer({ asset, onClose, onUpdate, onDelete, organisations = [], persons = [], onOrgsUpdated, onPersonsUpdated }) {
  const [editing, setEditing]           = useState(false);
  const [subjectType, setSubjectType]   = useState(asset.person_id ? 'person' : 'organisation');
  const [orgDialog, setOrgDialog]       = useState(false);
  const [personDialog, setPersonDialog] = useState(false);
  const [prefillName, setPrefillName]   = useState('');
  const [form, setForm] = useState({
    alt_text:          asset.alt_text || '',
    caption:           asset.caption || '',
    accreditation:     asset.source_attribution || '',
    tags:              asset.tags?.join(', ') || '',
    location:          asset.location || '',
    image_type:        asset.image_type || 'uploaded',
    organisation_id:   asset.organisation_id || '',
    organisation_name: organisations.find(o => o.id === asset.organisation_id)?.name || '',
    person_id:         asset.person_id || '',
    person_name:       persons.find(p => p.id === asset.person_id)?.name || '',
  });

  const handleSave = async () => {
    try {
      await mediaApi.updateAsset(asset.id, {
        alt_text:           form.alt_text,
        caption:            form.caption,
        source_attribution: form.accreditation,
        tags:               form.tags.split(',').map(t => t.trim()).filter(Boolean),
        location:           form.location,
        image_type:         form.image_type,
        organisation_id:    subjectType === 'organisation' ? (form.organisation_id || null) : null,
        person_id:          subjectType === 'person' ? (form.person_id || null) : null,
      });
      toast.success('Asset updated');
      onUpdate();
      setEditing(false);
    } catch {
      toast.error('Failed to update asset');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Deactivate this asset? It will no longer appear in the library.')) return;
    try {
      await mediaApi.deleteAsset(asset.id);
      toast.success('Asset deactivated');
      onDelete();
      onClose();
    } catch {
      toast.error('Failed to deactivate asset');
    }
  };

  const linkedOrg    = organisations.find(o => o.id === asset.organisation_id);
  const linkedPerson = persons.find(p => p.id === asset.person_id);

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-black/30" onClick={onClose} />
        <div className="relative bg-background w-full max-w-md h-full overflow-y-auto shadow-2xl border-l border-border p-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Asset Details</h3>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
          </div>

          <img
            src={asset.file_url}
            alt={asset.alt_text || asset.filename}
            className="w-full rounded-lg border border-border object-cover max-h-64"
            onError={(e) => { e.target.src = '/placeholder-image.png'; }}
          />

          <div className="text-sm text-muted-foreground space-y-1">
            <p><span className="font-medium text-foreground">File:</span> {asset.filename}</p>
            {asset.width > 0 && <p><span className="font-medium text-foreground">Size:</span> {asset.width}×{asset.height}px</p>}
            <p><span className="font-medium text-foreground">Orientation:</span> {asset.orientation}</p>
            <p><span className="font-medium text-foreground">Type:</span> {asset.image_type?.replace('_', ' ')}</p>
            <p><span className="font-medium text-foreground">Used:</span> {asset.usage_count} time{asset.usage_count !== 1 ? 's' : ''}</p>
            {asset.last_used_at && (
              <p><span className="font-medium text-foreground">Last used:</span> {new Date(asset.last_used_at).toLocaleDateString()}</p>
            )}
          </div>

          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">Linked to</label>
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => setSubjectType('organisation')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${subjectType === 'organisation' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
                  >
                    Organisation
                  </button>
                  <button
                    onClick={() => setSubjectType('person')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${subjectType === 'person' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
                  >
                    Person
                  </button>
                </div>
                {subjectType === 'organisation' && (
                  <EntitySearchCombobox
                    entityType="organisation"
                    value={form.organisation_id ? { id: form.organisation_id, name: form.organisation_name } : null}
                    onSelect={r => setForm(f => ({ ...f, organisation_id: r.id, organisation_name: r.name }))}
                    onClear={() => setForm(f => ({ ...f, organisation_id: '', organisation_name: '' }))}
                    onCreateRequest={name => { setPrefillName(name); setOrgDialog(true); }}
                    placeholder="Search or create organisation..."
                  />
                )}
                {subjectType === 'person' && (
                  <EntitySearchCombobox
                    entityType="person"
                    value={form.person_id ? { id: form.person_id, name: form.person_name } : null}
                    onSelect={r => setForm(f => ({ ...f, person_id: r.id, person_name: r.name }))}
                    onClear={() => setForm(f => ({ ...f, person_id: '', person_name: '' }))}
                    onCreateRequest={name => { setPrefillName(name); setPersonDialog(true); }}
                    placeholder="Search or create person..."
                  />
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-foreground">Alt text</label>
                <input
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  value={form.alt_text}
                  onChange={e => setForm(f => ({ ...f, alt_text: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground">Caption</label>
                <input
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  value={form.caption}
                  onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground">Accreditation</label>
                <input
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  placeholder="Accreditation"
                  value={form.accreditation}
                  onChange={e => setForm(f => ({ ...f, accreditation: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground">Tags (comma separated)</label>
                <input
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  value={form.tags}
                  onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground">Location</label>
                <input
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  value={form.location}
                  onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground">Type</label>
                <select
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  value={form.image_type}
                  onChange={e => setForm(f => ({ ...f, image_type: e.target.value }))}
                >
                  {IMAGE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-[#1F1A17] text-[#FFE4C4] rounded-lg py-2 text-sm font-medium hover:bg-[#2B241F]"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="flex-1 border border-border rounded-lg py-2 text-sm hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {asset.file_url && (
                <div className="flex items-center gap-2 bg-[#F8EBD9] rounded-lg px-3 py-2">
                  <p className="text-xs text-muted-foreground truncate flex-1 font-mono">
                    {asset.file_url}
                  </p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(asset.file_url);
                      toast.success('URL copied');
                    }}
                    className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                    title="Copy file URL"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              )}
              {linkedOrg && <p className="text-sm text-muted-foreground">🏢 {linkedOrg.name}</p>}
              {linkedPerson && (
                <p className="text-sm text-muted-foreground">
                  👤 {linkedPerson.name}{linkedPerson.title ? ` — ${linkedPerson.title}` : ''}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Caption:</span>{' '}
                {asset.caption ? <span className="italic">"{asset.caption}"</span> : <span>—</span>}
              </p>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Accreditation:</span>{' '}
                {asset.source_attribution || <span>—</span>}
              </p>
              {asset.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {asset.tags.map(tag => (
                    <span key={tag} className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">{tag}</span>
                  ))}
                </div>
              )}
              {asset.location && <p className="text-sm text-muted-foreground">📍 {asset.location}</p>}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-lg text-sm hover:bg-muted"
                >
                  <Edit3 size={14} /> Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50"
                >
                  <Trash2 size={14} /> Deactivate
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <OrgMatchDialog
        open={orgDialog}
        onOpenChange={setOrgDialog}
        prefillName={prefillName}
        onSelect={r => setForm(f => ({ ...f, organisation_id: r.id, organisation_name: r.name }))}
        setOrganisations={onOrgsUpdated}
      />
      <PersonMatchDialog
        open={personDialog}
        onOpenChange={setPersonDialog}
        prefillName={prefillName}
        onSelect={r => setForm(f => ({ ...f, person_id: r.id, person_name: r.name }))}
        organisations={organisations}
        setPersons={onPersonsUpdated}
      />
    </>
  );
}


function LibraryTab({ organisations = [], persons = [], onOrgsUpdated, onPersonsUpdated }) {
  const [assets, setAssets]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);
  const [offset, setOffset]     = useState(0);
  const limit = 24;

  // Filters always initialise to empty — resets on every page load/remount
  const [filters, setFilters]                   = useState({ ...EMPTY_FILTERS });
  const [filterSubjectType, setFilterSubjectType] = useState('organisation');

  // Debounce text inputs — avoids firing on every keystroke
  const debouncedSearch = useDebounce(filters.search, 350);
  const debouncedTags   = useDebounce(filters.tags, 350);

  // True while debounce is still counting down — drives the input spinner
  const searchPending = filters.search !== debouncedSearch;
  const tagsPending   = filters.tags   !== debouncedTags;

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (debouncedSearch)     params.search      = debouncedSearch;
      if (filters.image_type)  params.image_type  = filters.image_type;
      if (filters.orientation) params.orientation = filters.orientation;
      if (debouncedTags)       params.tags        = debouncedTags;
      if (filterSubjectType === 'organisation' && filters.organisation_id) params.organisation_id = filters.organisation_id;
      if (filterSubjectType === 'person'       && filters.person_id)       params.person_id       = filters.person_id;
      const res = await mediaApi.getAssets(params);
      setAssets(res.data.assets);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, debouncedTags, filters.image_type, filters.orientation,
      filters.organisation_id, filters.person_id, filterSubjectType, offset]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  // Reset to page 1 whenever any filter changes
  const setFilter = (key, value) => {
    setOffset(0);
    setFilters(f => ({ ...f, [key]: value }));
  };

  const clearEntityFilter = () => {
    setOffset(0);
    setFilters(f => ({ ...f, organisation_id: '', organisation_name: '', person_id: '', person_name: '' }));
  };

  const clearAllFilters = () => {
    setOffset(0);
    setFilters({ ...EMPTY_FILTERS });
    setFilterSubjectType('organisation');
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const totalPages = Math.ceil(total / limit);
  const page       = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-4">

      {/* Text + dropdown filters */}
      <div className="flex flex-wrap gap-3 items-start">

        {/* Filename / keyword search with spinner */}
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            className="w-full pl-8 pr-8 py-2 border border-border rounded-lg text-sm bg-background"
            placeholder="Search by filename, alt text, caption..."
            value={filters.search}
            onChange={e => setFilter('search', e.target.value)}
          />
          {(searchPending || (loading && filters.search)) && (
            <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>

        {/* Tag search with spinner */}
        <div className="relative min-w-48">
          <input
            className="w-full pl-3 pr-8 py-2 border border-border rounded-lg text-sm bg-background"
            placeholder="Tags — e.g. finance, Swindon"
            value={filters.tags}
            onChange={e => setFilter('tags', e.target.value)}
          />
          {(tagsPending || (loading && filters.tags)) && (
            <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>

        <select
          className="border border-border rounded-lg px-3 py-2 text-sm bg-background"
          value={filters.image_type}
          onChange={e => setFilter('image_type', e.target.value)}
        >
          <option value="">All types</option>
          {IMAGE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
        </select>

        <select
          className="border border-border rounded-lg px-3 py-2 text-sm bg-background"
          value={filters.orientation}
          onChange={e => setFilter('orientation', e.target.value)}
        >
          <option value="">All orientations</option>
          {ORIENTATIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>

        <button
          onClick={fetchAssets}
          className="p-2 border border-border rounded-lg hover:bg-muted"
          title="Refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>

        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={13} /> Clear filters
          </button>
        )}
      </div>

      {/* Entity filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          <button
            onClick={() => { setFilterSubjectType('organisation'); clearEntityFilter(); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${filterSubjectType === 'organisation' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
          >
            Organisation
          </button>
          <button
            onClick={() => { setFilterSubjectType('person'); clearEntityFilter(); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${filterSubjectType === 'person' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
          >
            Person
          </button>
        </div>
        <div className="flex-1 min-w-48 max-w-xs">
          {filterSubjectType === 'organisation' ? (
            <EntitySearchCombobox
              entityType="organisation"
              value={filters.organisation_id ? { id: filters.organisation_id, name: filters.organisation_name } : null}
              onSelect={r => { setOffset(0); setFilters(f => ({ ...f, organisation_id: r.id, organisation_name: r.name })); }}
              onClear={() => { setOffset(0); setFilters(f => ({ ...f, organisation_id: '', organisation_name: '' })); }}
              placeholder="Filter by organisation..."
            />
          ) : (
            <EntitySearchCombobox
              entityType="person"
              value={filters.person_id ? { id: filters.person_id, name: filters.person_name } : null}
              onSelect={r => { setOffset(0); setFilters(f => ({ ...f, person_id: r.id, person_name: r.name })); }}
              onClear={() => { setOffset(0); setFilters(f => ({ ...f, person_id: '', person_name: '' })); }}
              placeholder="Filter by person..."
            />
          )}
        </div>
      </div>

      {/* OR hint when tag filter is active */}
      {filters.tags && (
        <p className="text-xs text-muted-foreground">
          Showing images tagged with{' '}
          {filters.tags.split(',').map(t => t.trim()).filter(Boolean).map((t, idx, arr) => (
            <span key={t}>
              <span className="font-medium text-foreground">"{t}"</span>
              {idx < arr.length - 1 && <span className="text-muted-foreground"> or </span>}
            </span>
          ))}
          {(tagsPending || loading) && <Loader2 size={11} className="inline ml-1 animate-spin" />}
        </p>
      )}

      <p className="text-sm text-muted-foreground flex items-center gap-2">
        {total} asset{total !== 1 ? 's' : ''}
        {(loading || searchPending) && !filters.tags && <Loader2 size={13} className="animate-spin" />}
      </p>

      {loading && assets.length === 0 ? (
        <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading assets...
        </div>
      ) : assets.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">No assets found.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {assets.map(asset => (
            <AssetCard
              key={asset.id}
              asset={asset}
              selected={selected?.id === asset.id}
              onSelect={setSelected}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center gap-2 justify-center pt-4">
          <button
            disabled={page === 1}
            onClick={() => setOffset(o => Math.max(0, o - limit))}
            className="px-3 py-1.5 border border-border rounded-lg text-sm disabled:opacity-40 hover:bg-muted"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground">{page} of {totalPages}</span>
          <button
            disabled={page === totalPages}
            onClick={() => setOffset(o => o + limit)}
            className="px-3 py-1.5 border border-border rounded-lg text-sm disabled:opacity-40 hover:bg-muted"
          >
            Next
          </button>
        </div>
      )}

      {selected && (
        <AssetDrawer
          asset={selected}
          organisations={organisations}
          persons={persons}
          onClose={() => setSelected(null)}
          onUpdate={fetchAssets}
          onDelete={() => { fetchAssets(); setSelected(null); }}
          onOrgsUpdated={onOrgsUpdated}
          onPersonsUpdated={onPersonsUpdated}
        />
      )}
    </div>
  );
}


function UploadTab({ onUploaded, organisations = [], persons = [], onOrgsUpdated, onPersonsUpdated }) {
  const [files, setFiles]             = useState([]);
  const [dragging, setDragging]       = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [subjectType, setSubjectType] = useState('organisation');
  const [orgDialog, setOrgDialog]     = useState(false);
  const [personDialog, setPersonDialog] = useState(false);
  const [prefillName, setPrefillName] = useState('');

  const [form, setForm] = useState({
    image_type:       'uploaded',
    organisation_id:  '',
    organisation_name:'',
    person_id:        '',
    person_name:      '',
    tags:             '',
    location:         '',
    alt_text:         '',
    accreditation:    '',
  });
  const [perFile, setPerFile] = useState([]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/')));
  };
  const handleFileInput = (e) => addFiles(Array.from(e.target.files).filter(f => f.type.startsWith('image/')));

  const addFiles = (newFiles) => {
    setFiles(prev => [...prev, ...newFiles]);
    setPerFile(prev => [...prev, ...newFiles.map(() => ({ caption: '', accreditation: '' }))]);
  };
  const removeFile = (i) => {
    setFiles(prev => prev.filter((_, j) => j !== i));
    setPerFile(prev => prev.filter((_, j) => j !== i));
  };
  const updatePerFile = (i, field, value) => {
    setPerFile(prev => prev.map((f, j) => j === i ? { ...f, [field]: value } : f));
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    let successCount = 0, failCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const pf   = perFile[i] || {};
      const fd   = new FormData();
      fd.append('file', file);
      fd.append('image_type', form.image_type);
      fd.append('tags', form.tags);
      fd.append('location', form.location);
      fd.append('alt_text', form.alt_text || file.name);
      fd.append('caption', pf.caption || '');
      fd.append('source_attribution', pf.accreditation || form.accreditation || '');
      if (subjectType === 'organisation' && form.organisation_id) fd.append('organisation_id', form.organisation_id);
      if (subjectType === 'person' && form.person_id)             fd.append('person_id', form.person_id);
      try { await mediaApi.uploadAsset(fd); successCount++; }
      catch { failCount++; }
    }

    setUploading(false);
    setFiles([]);
    setPerFile([]);
    if (successCount) toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
    if (failCount)    toast.error(`${failCount} file${failCount > 1 ? 's' : ''} failed`);
    if (successCount) onUploaded();
  };

  const multipleFiles = files.length > 1;

  return (
    <>
      <div className="space-y-6 max-w-2xl">

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${dragging ? 'border-blue-400 bg-blue-50' : 'border-border hover:border-[#C9B49A]'}`}
        >
          <Upload size={32} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">Drop images here or click the button below</p>
          <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP, GIF, AVIF, HEIC, TIFF, BMP, SVG — max 50MB each</p>
          <label className="mt-4 inline-block cursor-pointer">
            <input type="file" multiple accept="image/*" onChange={handleFileInput} className="hidden" />
            <span className="mt-3 inline-block px-4 py-2 bg-[#FFE4C4] text-[#1F1A17] rounded-lg text-sm font-medium hover:bg-[#FFD4A8]">Browse files</span>
          </label>
        </div>

        {/* Selected files — caption & credit per image */}
        {files.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">
              {files.length} file{files.length > 1 ? 's' : ''} selected
              {multipleFiles && <span className="text-xs text-muted-foreground ml-2 font-normal">— caption & credit per image</span>}
            </p>
            {files.map((file, i) => {
              const previewUrl = URL.createObjectURL(file);
              return (
                <div key={i} className="bg-muted rounded-xl overflow-hidden border border-border">
                  <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border">
                    <span className="text-sm text-foreground flex-1 truncate font-medium">{file.name}</span>
                    <span className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)}KB</span>
                    <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-red-500">
                      <X size={14} />
                    </button>
                  </div>
                  <div className="flex gap-3 p-3">
                    <img
                      src={previewUrl}
                      alt={file.name}
                      className="w-28 h-20 object-cover rounded-lg shrink-0 border border-border"
                      onLoad={() => URL.revokeObjectURL(previewUrl)}
                    />
                    <div className="flex-1 space-y-2">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Caption</label>
                        <input
                          className="w-full border border-border rounded-lg px-3 py-1.5 text-sm bg-background"
                          placeholder="Image caption"
                          value={perFile[i]?.caption || ''}
                          onChange={e => updatePerFile(i, 'caption', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">
                          Accreditation <span className="font-normal opacity-70">— overrides default</span>
                        </label>
                        <input
                          className="w-full border border-border rounded-lg px-3 py-1.5 text-sm bg-background"
                          placeholder="Leave blank to use default below"
                          value={perFile[i]?.accreditation || ''}
                          onChange={e => updatePerFile(i, 'accreditation', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Shared metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-foreground">Type</label>
            <select
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
              value={form.image_type}
              onChange={e => setForm(f => ({ ...f, image_type: e.target.value }))}
            >
              {IMAGE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </div>

          <div className="col-span-2">
            <label className="text-xs font-medium text-foreground mb-1 block">Link to</label>
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setSubjectType('organisation')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${subjectType === 'organisation' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
              >
                Organisation
              </button>
              <button
                onClick={() => setSubjectType('person')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${subjectType === 'person' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
              >
                Person
              </button>
            </div>
            {subjectType === 'organisation' ? (
              <EntitySearchCombobox
                entityType="organisation"
                value={form.organisation_id ? { id: form.organisation_id, name: form.organisation_name } : null}
                onSelect={r => setForm(f => ({ ...f, organisation_id: r.id, organisation_name: r.name }))}
                onClear={() => setForm(f => ({ ...f, organisation_id: '', organisation_name: '' }))}
                onCreateRequest={name => { setPrefillName(name); setOrgDialog(true); }}
                placeholder="Search or create organisation..."
              />
            ) : (
              <EntitySearchCombobox
                entityType="person"
                value={form.person_id ? { id: form.person_id, name: form.person_name } : null}
                onSelect={r => setForm(f => ({ ...f, person_id: r.id, person_name: r.name }))}
                onClear={() => setForm(f => ({ ...f, person_id: '', person_name: '' }))}
                onCreateRequest={name => { setPrefillName(name); setPersonDialog(true); }}
                placeholder="Search or create person..."
              />
            )}
          </div>

          <div className="col-span-2">
            <label className="text-xs font-medium text-foreground">
              Tags (comma separated)
              {multipleFiles && <span className="text-muted-foreground font-normal ml-1">— shared across all files</span>}
            </label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
              placeholder="e.g. finance, banking, london"
              value={form.tags}
              onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-foreground">
              Alt text
              {multipleFiles && <span className="text-muted-foreground font-normal ml-1">— shared across all files</span>}
            </label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
              placeholder="Descriptive text for the image"
              value={form.alt_text}
              onChange={e => setForm(f => ({ ...f, alt_text: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground">
              Location
              {multipleFiles && <span className="text-muted-foreground font-normal ml-1">— shared</span>}
            </label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
              placeholder="e.g. London, UK"
              value={form.location}
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground">
              Accreditation
              {multipleFiles && <span className="text-muted-foreground font-normal ml-1">— default for all</span>}
            </label>
            <input
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
              placeholder={multipleFiles ? 'Applied to images with no credit set above' : 'Accreditation'}
              value={form.accreditation}
              onChange={e => setForm(f => ({ ...f, accreditation: e.target.value }))}
            />
          </div>
        </div>

        <button
          onClick={handleUpload}
          disabled={!files.length || uploading}
          className="w-full bg-[#1F1A17] text-[#FFE4C4] rounded-lg py-2.5 text-sm font-medium hover:bg-[#2B241F] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {uploading
            ? <><RefreshCw size={14} className="animate-spin" />Uploading...</>
            : <><Upload size={14} />Upload {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''}` : 'files'}</>
          }
        </button>
      </div>

      <OrgMatchDialog
        open={orgDialog}
        onOpenChange={setOrgDialog}
        prefillName={prefillName}
        onSelect={r => setForm(f => ({ ...f, organisation_id: r.id, organisation_name: r.name }))}
        setOrganisations={onOrgsUpdated}
      />
      <PersonMatchDialog
        open={personDialog}
        onOpenChange={setPersonDialog}
        prefillName={prefillName}
        onSelect={r => setForm(f => ({ ...f, person_id: r.id, person_name: r.name }))}
        organisations={organisations}
        setPersons={onPersonsUpdated}
      />
    </>
  );
}


function AssignTab({ organisations = [], persons = [], publications = [] }) {
  const [subjectType, setSubjectType] = useState('organisation');
  const [params, setParams] = useState({
    organisation_id:   '',
    organisation_name: '',
    person_id:         '',
    person_name:       '',
    tags:              '',
    publication_id:    '',
    image_type:        '',
  });
  const [result, setResult]   = useState(undefined);
  const [loading, setLoading] = useState(false);

  const runAssign = async (currentParams, currentSubjectType) => {
    setLoading(true);
    try {
      const query = {};
      if (currentSubjectType === 'organisation' && currentParams.organisation_id) query.organisation_id = currentParams.organisation_id;
      if (currentSubjectType === 'person' && currentParams.person_id)             query.person_id       = currentParams.person_id;
      if (currentParams.tags)           query.tags           = currentParams.tags;
      if (currentParams.publication_id) query.publication_id = currentParams.publication_id;
      if (currentParams.image_type)     query.image_type     = currentParams.image_type;
      const res = await mediaApi.assignImage(query);
      setResult(res.data.asset || null);
    } catch {
      toast.error('Assignment failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePersonSelect = (r) => { const next = { ...params, person_id: r.id, person_name: r.name }; setParams(next); runAssign(next, 'person'); };
  const handleOrgSelect    = (r) => { const next = { ...params, organisation_id: r.id, organisation_name: r.name }; setParams(next); runAssign(next, 'organisation'); };

  return (
    <div className="space-y-6 max-w-xl">
      <p className="text-sm text-muted-foreground">Preview what the assignment engine would return for a given story context.</p>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="text-xs font-medium text-foreground mb-1 block">Subject</label>
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => { setSubjectType('organisation'); setResult(undefined); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${subjectType === 'organisation' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
            >
              Organisation
            </button>
            <button
              onClick={() => { setSubjectType('person'); setResult(undefined); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${subjectType === 'person' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
            >
              Person
            </button>
          </div>
          {subjectType === 'organisation' ? (
            <EntitySearchCombobox
              entityType="organisation"
              value={params.organisation_id ? { id: params.organisation_id, name: params.organisation_name } : null}
              onSelect={handleOrgSelect}
              onClear={() => { setParams(p => ({ ...p, organisation_id: '', organisation_name: '' })); setResult(undefined); }}
              placeholder="Search organisations..."
            />
          ) : (
            <EntitySearchCombobox
              entityType="person"
              value={params.person_id ? { id: params.person_id, name: params.person_name } : null}
              onSelect={handlePersonSelect}
              onClear={() => { setParams(p => ({ ...p, person_id: '', person_name: '' })); setResult(undefined); }}
              placeholder="Search persons..."
            />
          )}
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium text-foreground">Publication</label>
          <select
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
            value={params.publication_id}
            onChange={e => setParams(p => ({ ...p, publication_id: e.target.value }))}
          >
            <option value="">Any publication</option>
            {publications.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-foreground">Image type</label>
          <select
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
            value={params.image_type}
            onChange={e => setParams(p => ({ ...p, image_type: e.target.value }))}
          >
            <option value="">Any</option>
            {IMAGE_TYPES.filter(t => t !== 'logo').map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-foreground">Tags</label>
          <input
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
            placeholder="finance, banking"
            value={params.tags}
            onChange={e => setParams(p => ({ ...p, tags: e.target.value }))}
          />
        </div>
      </div>
      <button
        onClick={() => runAssign(params, subjectType)}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 bg-[#1F1A17] text-[#FFE4C4] rounded-lg text-sm font-medium hover:bg-[#2B241F] disabled:opacity-40"
      >
        {loading ? <RefreshCw size={14} className="animate-spin" /> : <Wand2 size={14} />}
        Run assignment engine
      </button>
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 size={14} className="animate-spin" /> Finding best match...
        </div>
      )}
      {result === null && !loading && <p className="text-sm text-muted-foreground">No asset found for these parameters.</p>}
      {result && (
        <div className="border border-border rounded-xl overflow-hidden">
          <img
            src={result.thumbnail_url || result.file_url}
            alt={result.alt_text}
            className="w-full max-h-48 object-cover"
            onError={(e) => { e.target.src = '/placeholder-image.png'; }}
          />
          <div className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <TypeBadge type={result.image_type} />
              <OrientationIcon orientation={result.orientation} />
            </div>
            <p className="text-sm font-medium text-foreground">{result.filename}</p>
            <p className="text-xs text-muted-foreground">Used {result.usage_count} times</p>
            {result.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {result.tags.map(tag => (
                  <span key={tag} className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function AIGenerateTab() {
  const [prompt, setPrompt]           = useState('');
  const [generating, setGenerating]   = useState(false);
  const [fillingMeta, setFillingMeta] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [preview, setPreview]         = useState(null);
  const [saved, setSaved]             = useState(false);
  const [orgDialog, setOrgDialog]     = useState(false);
  const [personDialog, setPersonDialog] = useState(false);
  const [prefillName, setPrefillName]   = useState('');
  const [organisations, setOrganisations] = useState([]);
  const [persons, setPersons]             = useState([]);
  const [meta, setMeta] = useState({
    name:              '',
    caption:           '',
    source_attribution:'AI-generated image',
    alt_text:          '',
    tags:              '',
    location:          '',
    subject_type:      'organisation',
    organisation_id:   '',
    organisation_name: '',
    person_id:         '',
    person_name:       '',
  });

  useEffect(() => {
    api.get('/organisations').then(r => setOrganisations(r.data)).catch(() => {});
    api.get('/persons').then(r => setPersons(r.data)).catch(() => {});
  }, []);

  const autoFillMeta = async (promptText) => {
    if (!promptText.trim()) return;
    setFillingMeta(true);
    try {
      const res = await api.post('/stories/generate-draft', {
        source_link:        `data:text/plain,${encodeURIComponent(promptText)}`,
        story_subject_type: 'organisation',
      });
      const d = res.data;
      setMeta(m => ({
        ...m,
        caption:  d.image_caption  || m.caption,
        alt_text: d.image_alt_text || m.alt_text,
        tags:     d.image_tags?.length ? d.image_tags.join(', ') : m.tags,
      }));
    } catch {
      // non-fatal
    } finally {
      setFillingMeta(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setPreview(null);
    setSaved(false);
    try {
      const res = await api.post('/image-gen/generate', { prompt: prompt.trim() });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      await api.post('/image-gen/save', {
        ...preview,
        name:               meta.name,
        caption:            meta.caption,
        source_attribution: meta.source_attribution,
        alt_text:           meta.alt_text,
        tags:               meta.tags,
        location:           meta.location,
        organisation_id:    meta.subject_type === 'organisation' ? (meta.organisation_id || null) : null,
        person_id:          meta.subject_type === 'person' ? (meta.person_id || null) : null,
      });
      setSaved(true);
      toast.success('Image saved to media library');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save image');
    } finally {
      setSaving(false);
    }
  };

  const updateMeta = (field, value) => setMeta(m => ({ ...m, [field]: value }));

  return (
    <>
      <div className="max-w-2xl space-y-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles size={14} className="text-amber-500" />
          <span>Generate an image using Seedream 5.0 Lite</span>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-foreground mb-1 block">Prompt *</label>
            <div className="relative">
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onBlur={e => autoFillMeta(e.target.value)}
                placeholder='e.g. "A busy London financial district skyline at dusk, aerial view, editorial style"'
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background resize-none min-h-[90px] placeholder:text-muted-foreground"
              />
              {fillingMeta && (
                <div className="absolute bottom-2 right-2 flex items-center gap-1 text-xs text-amber-600">
                  <Loader2 size={11} className="animate-spin" /> Auto-filling...
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Fields below will auto-fill when you finish typing the prompt.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-medium text-foreground">Name</label>
              <input
                className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                placeholder="e.g. london-skyline-dusk"
                value={meta.name}
                onChange={e => updateMeta('name', e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground">Caption</label>
              <input
                className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                placeholder="Image caption"
                value={meta.caption}
                onChange={e => updateMeta('caption', e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground">Alt text</label>
              <input
                className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                placeholder="Descriptive alt text"
                value={meta.alt_text}
                onChange={e => updateMeta('alt_text', e.target.value)}
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-foreground">
                Tags <span className="text-muted-foreground font-normal">(comma separated)</span>
              </label>
              <input
                className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                placeholder="e.g. finance, london, skyline"
                value={meta.tags}
                onChange={e => updateMeta('tags', e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground">Location</label>
              <input
                className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                placeholder="e.g. London, UK"
                value={meta.location}
                onChange={e => updateMeta('location', e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground">Accreditation</label>
              <input
                className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                placeholder="Accreditation"
                value={meta.source_attribution}
                onChange={e => updateMeta('source_attribution', e.target.value)}
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-foreground mb-1 block">Link to</label>
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => updateMeta('subject_type', 'organisation')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${meta.subject_type === 'organisation' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
                >
                  Organisation
                </button>
                <button
                  onClick={() => updateMeta('subject_type', 'person')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${meta.subject_type === 'person' ? 'bg-[#1F1A17] text-[#FFE4C4] border-[#1F1A17]' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
                >
                  Person
                </button>
              </div>
              {meta.subject_type === 'organisation' ? (
                <EntitySearchCombobox
                  entityType="organisation"
                  value={meta.organisation_id ? { id: meta.organisation_id, name: meta.organisation_name } : null}
                  onSelect={r => setMeta(m => ({ ...m, organisation_id: r.id, organisation_name: r.name }))}
                  onClear={() => setMeta(m => ({ ...m, organisation_id: '', organisation_name: '' }))}
                  onCreateRequest={name => { setPrefillName(name); setOrgDialog(true); }}
                  placeholder="Search or create organisation..."
                />
              ) : (
                <EntitySearchCombobox
                  entityType="person"
                  value={meta.person_id ? { id: meta.person_id, name: meta.person_name } : null}
                  onSelect={r => setMeta(m => ({ ...m, person_id: r.id, person_name: r.name }))}
                  onClear={() => setMeta(m => ({ ...m, person_id: '', person_name: '' }))}
                  onCreateRequest={name => { setPrefillName(name); setPersonDialog(true); }}
                  placeholder="Search or create person..."
                />
              )}
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={!prompt.trim() || generating}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#1F1A17] text-[#FFE4C4] rounded-lg text-sm font-medium hover:bg-[#2B241F] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {generating
              ? <><RefreshCw size={14} className="animate-spin" />Generating...</>
              : <><Sparkles size={14} />Generate Image</>
            }
          </button>
        </div>

        {preview && (
          <div className="space-y-4 border-t border-border pt-6">
            <img
              src={`data:image/png;base64,${preview.b64_image}`}
              alt="AI generated preview"
              className="w-full rounded-xl border border-border object-cover aspect-video max-w-xl"
            />
            <div className="flex gap-2 max-w-xl">
              {saved ? (
                <p className="text-xs text-emerald-600 flex items-center gap-1.5 py-2">
                  <Check size={13} /> Saved to media library
                </p>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1F1A17] text-[#FFE4C4] rounded-lg text-sm font-medium hover:bg-[#2B241F] disabled:opacity-40"
                >
                  {saving
                    ? <><RefreshCw size={13} className="animate-spin" />Saving...</>
                    : <><Check size={13} />Save to library</>
                  }
                </button>
              )}
              <button
                onClick={() => { setPreview(null); setSaved(false); }}
                className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted"
              >
                Generate another
              </button>
            </div>
          </div>
        )}
      </div>

      <OrgMatchDialog
        open={orgDialog}
        onOpenChange={setOrgDialog}
        prefillName={prefillName}
        onSelect={r => setMeta(m => ({ ...m, organisation_id: r.id, organisation_name: r.name }))}
        setOrganisations={org => setOrganisations(prev => [...prev, org])}
      />
      <PersonMatchDialog
        open={personDialog}
        onOpenChange={setPersonDialog}
        prefillName={prefillName}
        onSelect={r => setMeta(m => ({ ...m, person_id: r.id, person_name: r.name }))}
        organisations={organisations}
        setPersons={p => setPersons(prev => [...prev, p])}
      />
    </>
  );
}

export default function MediaPage() {
  const [activeTab, setActiveTab]         = useState('library');
  const [organisations, setOrganisations] = useState([]);
  const [persons, setPersons]             = useState([]);
  const [publications, setPublications]   = useState([]);

  useEffect(() => {
    api.get('/organisations').then(res => setOrganisations(res.data)).catch(() => {});
    api.get('/persons').then(res => setPersons(res.data)).catch(() => {});
    api.get('/publications').then(res => setPublications(res.data)).catch(() => {});
  }, []);

  const tabs = [
    { id: 'library',  label: 'Library',       icon: Image    },
    { id: 'upload',   label: 'Upload',         icon: Upload   },
    { id: 'assign',   label: 'Assign Preview', icon: Wand2    },
    { id: 'generate', label: 'AI Generate',    icon: Sparkles },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground font-outfit">Media Library</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage and assign images across all publications</p>
      </div>

      <div className="flex gap-1 bg-[#FFF4E8] p-1 rounded-xl border border-border mb-6 w-fit">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-white text-[#1F1A17] shadow-sm border border-[#E7D2B8]'
                : id === 'generate'
                  ? 'text-amber-500 hover:text-amber-600'
                  : 'text-[#6E6258] hover:text-[#1F1A17]'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'library' && (
          <LibraryTab
            organisations={organisations}
            persons={persons}
            onOrgsUpdated={org => setOrganisations(prev => [...prev, org])}
            onPersonsUpdated={p => setPersons(prev => [...prev, p])}
          />
        )}
        {activeTab === 'upload' && (
          <UploadTab
            onUploaded={() => setActiveTab('library')}
            organisations={organisations}
            persons={persons}
            onOrgsUpdated={org => setOrganisations(prev => [...prev, org])}
            onPersonsUpdated={p => setPersons(prev => [...prev, p])}
          />
        )}
        {activeTab === 'assign'   && <AssignTab organisations={organisations} persons={persons} publications={publications} />}
        {activeTab === 'generate' && <AIGenerateTab />}
      </div>
    </div>
  );
}